'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';

type MHInputVariant = 'default' | 'sidebarSearch' | 'composer';

export interface MHInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  floatingLabel?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  variant?: MHInputVariant;
}

export interface MHFloatingLabelInputProps extends MHInputProps {
  label: string;
}

const MHInput = React.forwardRef<HTMLInputElement, MHInputProps>(
  (
    {
      className,
      type,
      label,
      floatingLabel = false,
      wrapperClassName,
      labelClassName,
      id,
      placeholder,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const [isFocused, setIsFocused] = React.useState(false);
    const [uncontrolledValue, setUncontrolledValue] = React.useState(
      typeof defaultValue === 'string' ? defaultValue : ''
    );

    const resolvedValue = typeof value === 'string' ? value : uncontrolledValue;
    const hasValue = resolvedValue.trim().length > 0;
    const shouldFloat = Boolean(label && floatingLabel && (isFocused || hasValue));

    if (!floatingLabel || !label) {
      const isComposer = variant === 'composer';
      const isSidebarSearch = variant === 'sidebarSearch';

      return (
        <input
          id={inputId}
          type={type}
          className={cn(
            'flex h-11 w-full rounded-full border border-border-subtle bg-surface-2 px-4 text-body text-text-primary caret-ring',
            isSidebarSearch && 'h-11 border-0 bg-bg-0 text-body text-text-1 placeholder:text-text-3',
            isComposer && 'h-11 border-0 bg-transparent px-0 py-0 text-body text-text-1 placeholder:text-text-3 hover:bg-transparent focus:border-0 focus-visible:border-0',
            'file:border-0 file:bg-transparent file:text-body file:font-medium file:text-text-primary',
            'placeholder:text-text-tertiary',
            !isComposer && 'hover:border-border-strong hover:bg-surface-3',
            'focus:outline-none focus:ring-0 focus-visible:ring-0',
            !isComposer && 'focus:border-focus-1 focus-visible:border-focus-1',
            'disabled:cursor-not-allowed disabled:opacity-[0.45]',
            className
          )}
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          onChange={(event) => {
            if (value === undefined) {
              setUncontrolledValue(event.target.value);
            }
            onChange?.(event);
          }}
          onFocus={(event) => {
            setIsFocused(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setIsFocused(false);
            onBlur?.(event);
          }}
          {...props}
        />
      );
    }

    const isComposer = variant === 'composer';
    const isSidebarSearch = variant === 'sidebarSearch';

    return (
      <div className={cn('relative pt-2', wrapperClassName)}>
        <input
          id={inputId}
          type={type}
          ref={ref}
          className={cn(
            'peer flex h-11 w-full rounded-full border bg-surface-2 px-4 text-body text-text-primary caret-ring',
            isSidebarSearch && 'h-11 border-0 bg-bg-0 text-body text-text-1 placeholder:text-text-3',
            isComposer && 'h-11 border-0 bg-transparent px-0 py-0 text-body text-text-1 placeholder:text-text-3 hover:bg-transparent focus:border-0 focus-visible:border-0',
            !isComposer && (isFocused ? 'border-focus-1' : 'border-border-subtle hover:border-border-strong hover:bg-surface-3'),
            'file:border-0 file:bg-transparent file:text-body file:font-medium file:text-text-primary',
            'placeholder:text-text-tertiary focus:outline-none focus:ring-0 focus-visible:ring-0',
            'disabled:cursor-not-allowed disabled:opacity-[0.45]',
            className
          )}
          value={value}
          defaultValue={defaultValue}
          placeholder={shouldFloat ? '' : (placeholder ?? label)}
          onChange={(event) => {
            if (value === undefined) {
              setUncontrolledValue(event.target.value);
            }
            onChange?.(event);
          }}
          onFocus={(event) => {
            setIsFocused(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setIsFocused(false);
            onBlur?.(event);
          }}
          {...props}
        />
        {shouldFloat ? (
          <label
            htmlFor={inputId}
            className={cn(
              'pointer-events-none absolute left-4 top-0 z-[1] -translate-y-1/2 bg-transparent px-2 text-caption text-text-3',
              labelClassName
            )}
          >
            {label}
          </label>
        ) : null}
      </div>
    );
  }
);

MHInput.displayName = 'MHInput';

const MHFloatingLabelInput = React.forwardRef<HTMLInputElement, MHFloatingLabelInputProps>(
  ({ label, floatingLabel, ...props }, ref) => {
    return <MHInput ref={ref} label={label} floatingLabel={floatingLabel ?? true} {...props} />;
  }
);

MHFloatingLabelInput.displayName = 'MHFloatingLabelInput';

export { MHInput, MHFloatingLabelInput };
